import Callout from '../callout/callout'
import Loader from '../loaders/loader'
import InputField from '../inputs/inputField'
import { ArrowRightIcon, TwitterIcon } from '../icons'
import Modal from '../modalsAndOverlays/modal'
import {
	TypographyCaps,
	TypographyDisplay,
	TypographyHeading,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'
import Button from '../buttons/button'
import { Fragment, useEffect, useState } from 'react'
import Image from '../content/Image'
import TweetImport from './tweetImport.tsx'
import xBanner from '#images/xbanner.webp'
import TweetPreview from './tweetPreview.tsx'

export default function TweetImporter() {
	const [aboutModalOpen, setAboutModalOpen] = useState(false)
	const [tweetURL, setTweetURL] = useState('')
	const [tweet, setTweet] = useState({})
	const [importStage, setImportStage] = useState('start') // stages are "start", "importing", "preview" and "done"

	useEffect(() => {
		// Simulate importing process
		if (importStage == 'importing') {
			importTweet()
		}
	}, [importStage])

	useEffect(() => {
		if (tweet.arweaveCID && !tweet.timestamp) {
			setImportStage('preview')
			getArweaveData(tweet.arweaveCID)
		}
	}, [tweet])

	useEffect(() => {
		if (tweet.arweaveCID && !tweet.timestamp) {
			setImportStage('preview')
			getArweaveData(tweet.arweaveCID)
		}
	}, [tweet])

	const postImportTweet = async (tweetURL: string) => {
		const options = {
			method: 'GET',
			headers: { 'Content-Type': 'application/json' },
		}
		try {
			const response = await fetch(`/send-tweet?url=${tweetURL}`, options)
			return await response.json()
		} catch (e) {
			console.error(e)
			return null
		}
	}

	const getArweaveTransaction = async (transactionId: string) => {
		const options = { method: 'GET', headers: { accept: 'application/json' } }
		try {
			// @ts-ignore
			const response = await fetch(
				`https://arweave.net/tx/${transactionId}`,
				options,
			)
			return await response.json()
		} catch (e) {
			console.error(e)
			return null
		}
	}

	const getArweaveTransactionTimestamp = async (transactionId: string) => {
		const query = `
            {
                transaction(id: "${transactionId}") {
                    block {
                        id
                        timestamp
                    }
                }
            }
            `
		const options = {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ query }),
		}
		try {
			// @ts-ignore
			const response = await fetch(`https://arweave.net/graphql`, options)
			return await response.json()
		} catch (e) {
			console.error(e)
			return null
		}
	}

	const decodeArweaveTags = array => {
		return array
			.map(item => ({
				name: atob(item.name),
				value: atob(item.value),
			}))
			.reduce((obj, item) => {
				obj[item.name] = item.value
				return obj
			}, {})
	}

	const importTweet = () => {
		postImportTweet(tweetURL)
			.then(apiResponse => {
				if (!apiResponse) {
					setImportStage('start')
					return
				}
				const arweaveSegments = apiResponse.arweaveCID.split('/')
				const arweaveCID = arweaveSegments[arweaveSegments.length - 1]
				const ipfsSegments = apiResponse.ipfsCID.split('/')
				const ipfsCID = ipfsSegments[ipfsSegments.length - 1]
				const newTweet = {
					image: apiResponse.ipfsCID, // Can use`https://arweave.net/${arweaveCID}` or apiResponse.ipfsCID, latter is better since IPFS returns earlier in the process,
					ipfsCID: ipfsCID,
					ipfsURL: apiResponse.ipfsCID,
					arweaveCID: arweaveCID,
					arweaveURL: apiResponse.arweaveCID,
				}
				setTweet(newTweet)
			})
			// TODO add proper error handling with UI feedback
			.catch(e => {
				console.error(e)
				setImportStage('start')
			})
	}

	const getArweaveData = arweaveCID => {
		getArweaveTransaction(arweaveCID).then(arweaveResponse => {
			if (!arweaveResponse) {
				console.log("Retrying since Arweave's transaction is still pending")
				setTimeout(getArweaveData, 10000, arweaveCID)
				return
			}
			getArweaveTransactionTimestamp(arweaveCID).then(blockResponse => {
				const decodedTags = decodeArweaveTags(arweaveResponse.tags)
				setTweet({
					...tweet,
					timestamp: blockResponse.data.transaction.block.timestamp,
					publisher: decodedTags['Publisher'],
					app: decodedTags['AF-App'],
					template: decodedTags['AF-Template'],
					contentType: decodedTags['Content-Type'],
					platform: decodedTags['Social-Platform'],
					username: decodedTags['Social-Username'],
					url: decodedTags['Social-URL'],
					signature: arweaveResponse.signature,
				})
				setTweetURL('')
				setImportStage('done')
			})
		})
	}

	return (
		<div className="mx-auto max-w-[1440px] px-6 py-16 sm:px-6 md:px-12 lg:px-36">
			<div className="flex flex-row justify-between">
				<div className="flex w-5/12 flex-col">
					<TypographyCaps
						size="medium"
						additionalClasses="mb-4 text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Atomic Form
					</TypographyCaps>
					<TypographyDisplay
						size="large"
						additionalClasses="mb-4 text-textPrimaryLight dark:text-textPrimaryDark"
					>
						Tweet importer
					</TypographyDisplay>
					<TypographyLabel
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark w-2/3"
					>
						Data is permanently stored on Arweave & accessible through IPFS
					</TypographyLabel>
				</div>
				<div className="flex w-5/12 flex-col justify-end">
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark text-right"
					>
						Whether you want to preserve a tweet, commemorate a life event, or
						simply relive the highlights of your Twitter journey, this tool
						empowers you to capture, timestamp, and immortalize your digital
						memories.{' '}
						<Button
							variant="tertiary"
							additionalClasses="inline underline h-5 py-0"
							onClick={() => setAboutModalOpen(true)}
						>
							Learn more
						</Button>
						.
					</TypographyParagraph>
				</div>
			</div>
			<div className="mt-16 flex flex-row justify-between">
				{importStage == 'start' && (
					<div className="flex w-2/5 flex-col">
						<TypographyHeading
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Social Post URL
						</TypographyHeading>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark pr-20 mt-4"
						>
							Simply paste the URL of the tweet you want to import, including
							https://
						</TypographyParagraph>
					</div>
				)}
				{importStage == 'importing' && (
					<div className="flex w-2/5 flex-col">
						<TypographyHeading
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Importing Twitter Post...
						</TypographyHeading>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark pr-20 mt-4"
						>
							Sit tight! This process may take up to 2 minutes, as we ensure
							precision in securing every detail. Please don’t close this
							window.
						</TypographyParagraph>
					</div>
				)}
				{importStage == 'preview' && (
					<div className="flex w-2/5 flex-col">
						<TypographyHeading
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Timestamping Tweet...
						</TypographyHeading>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark pr-20 mt-4"
						>
							<b>This process may take up to 1 minute</b>, although occasional
							delays of up to <b>50 minutes</b> can occur in Arweave. Don't
							worry if it takes a little longer – we're ensuring every detail is
							accurately captured.
						</TypographyParagraph>
						<Callout
							variant="help"
							content="To track the transaction status, open the Airwave link provided in a new window. This allows you to monitor the progress and ensure a seamless process. Feel free to continue importing tweets while your memories are being securely timestamped."
							additionalClasses="mt-4 mr-20"
						/>
					</div>
				)}
				{importStage == 'done' && (
					<div className="flex w-2/5 flex-col">
						<TypographyCaps size="medium" additionalClasses="text-[#1AB51A]">
							Success!
						</TypographyCaps>
						<TypographyHeading
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark mt-4"
						>
							You have timestamped a tweet!
						</TypographyHeading>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark pr-20 mt-4"
						>
							If you have more tweets to capture, simply repeat the process, and
							let us help you build a timeless archive of your favorite Twitter
							moments.
						</TypographyParagraph>
						<div
							className="group flex h-[26px] w-full pt-12"
							onClick={e => setImportStage('start')}
						>
							<ArrowRightIcon className="mr-2 h-4 w-4 text-textPrimaryLight dark:text-textPrimaryDark" />
							<TypographyLabel
								size="large"
								isBold={true}
								additionalClasses="group-hover:cursor-pointer group-hover:underline text-textPrimaryLight dark:text-textPrimaryDark"
							>
								Import anothet Tweet
							</TypographyLabel>
						</div>
					</div>
				)}
				<div className="flex w-3/5 flex-col">
					{(importStage == 'start' || importStage == 'importing') && (
						<>
							<InputField
								name="Social Post URL (*)"
								placeholder="https://"
								additionalClasses="w-full"
								containerWidth="w-full"
								value={tweetURL}
								onChange={e => setTweetURL(e.target.value)}
								isInactive={importStage != 'start'}
							></InputField>
							<div className="mt-4 flex flex-row items-center">
								<TypographyLabel
									size="small"
									additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark mr-2"
								>
									We currently support links from{' '}
								</TypographyLabel>
								<TwitterIcon className="text-textSecondaryLight dark:text-textSecondaryDark" />
							</div>
						</>
					)}
					{importStage == 'start' && (
						<Button
							variant="primary"
							additionalClasses="mt-16"
							disabled={
								tweetURL.length && tweetURL.startsWith('https://')
									? false
									: true
							}
							onClick={() => {
								setImportStage('importing')
							}}
						>
							Import
						</Button>
					)}
					{importStage == 'importing' && (
						<div className="flex flex-row items-center justify-center">
							<Loader />
						</div>
					)}
					{importStage == 'preview' && tweet && <TweetPreview tweet={tweet} />}
					{importStage == 'done' && tweet && <TweetImport tweet={tweet} />}
				</div>
			</div>
			<Modal
				size="large"
				title="About Tweet Importer"
				body={
					<Fragment>
						<div className="mb-8 mt-6 flex flex-row items-center justify-center rounded-md bg-black">
							<Image
								className="h-32 w-auto object-cover"
								imageUrl={xBanner}
								imageAlt={'X logo'}
							></Image>
						</div>
						<p className="text-textSecondaryLight dark:text-textSecondaryDark">
							<span className="text-textPrimaryLight dark:text-textPrimaryDark">
								1. Airwave Integration:
							</span>{' '}
							The integration with Airwave, a robust data capturing technology,
							ensures that your tweets are captured with unparalleled accuracy.
						</p>
						<p className="text-textSecondaryLight dark:text-textSecondaryDark">
							<span className="text-textPrimaryLight dark:text-textPrimaryDark">
								2. IPFS Timestamping:
							</span>{' '}
							Security and authenticity are paramount when it comes to
							preserving digital memories. Tweet Importer utilizes IPFS to
							timestamp your imported tweets, creating an immutable record that
							is resistant to tampering. This ensures that your moments remain
							genuine and unaltered over time.
						</p>
						<p className="text-textSecondaryLight dark:text-textSecondaryDark">
							<span className="text-textPrimaryLight dark:text-textPrimaryDark">
								3. Decentralized Storage:
							</span>{' '}
							Your timestamped tweets are distributed across a network of nodes,
							enhancing data resilience and making them virtually impervious to
							data loss or censorship.
						</p>
						<p className="text-textSecondaryLight dark:text-textSecondaryDark">
							<span className="text-textPrimaryLight dark:text-textPrimaryDark">
								4. Transaction Fee:
							</span>{' '}
							We've got you covered! The transaction cost it’s on us 😉
						</p>
					</Fragment>
				}
				isOpen={aboutModalOpen}
				onClose={e => setAboutModalOpen(false)}
			/>
		</div>
	)
}
